import {
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  output,
  signal,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { SiaActionFired, SiaActionsService } from '@sia/actions';
import { SiaSelectBoxComponent } from '@sia/inputs';
import {
  FileService,
  jsonDeepClone,
  OutsideClickDirective,
  SiaArchivePath,
  SiaBaseScriptComponent,
  SiaFileArchive,
  SiaRouteData,
  SiaTranslatePipe,
} from '@sia/shared';
import { Subscription } from 'rxjs';
import { SiaCoreFileSystemComponent } from '../sia-core-file-system/sia-core-file-system.component';

@Component({
  selector: 'sia-file-system-panel',
  templateUrl: './sia-file-system-panel.component.html',
  styleUrls: ['./sia-file-system-panel.component.scss'],
  standalone: true,
  imports: [
    SiaCoreFileSystemComponent,
    SiaSelectBoxComponent,
    OutsideClickDirective,
    SiaTranslatePipe,
    MatIconButton,
    MatIcon,
  ],
})
export class SiaFileSystemPanelComponent extends SiaBaseScriptComponent {
  private siaActionsService = inject(SiaActionsService);
  private siaFileSystemService = inject(FileService);
  private cdr = inject(ChangeDetectorRef);

  private _pageChangedSub?: Subscription;
  private _pageData?: SiaRouteData = {};
  private _subs: Subscription[] = [];
  private _selectedArchive: SiaFileArchive | null | undefined;
  archivesSelectHeight: number = 0;
  #lastArchivesPaths?: string;
  previewVisible = signal<boolean>(false);

  get selectedArchive(): SiaFileArchive | null | undefined {
    return this._selectedArchive;
  }
  set selectedArchive(value: SiaFileArchive | null | undefined) {
    if (this._selectedArchive == value || value == null || !value) {
      return;
    }
    this._selectedArchive = value;
    this.path = jsonDeepClone({
      path: value?.path ?? '',
      isRelativePath: value?.isRelativePath,
      id: value?.id,
    });
  }

  visible: boolean = false;
  @ViewChild('selectBoxArchivesEl', { read: ViewContainerRef })
  selectBoxArchives: any;

  hasArchives = output<boolean>();
  path?: SiaArchivePath | undefined | null;
  archives: SiaFileArchive[] = [];
  archivesDataSource: { id?: string; description?: string }[] = [];

  @Input() set pageData(value: SiaRouteData | undefined) {
    if (
      this._pageData?.componentId == value?.componentId &&
      this._pageData?.guid == value?.guid
    ) {
      return;
    }
    this._pageData = jsonDeepClone(value);
    this.baseRouterPageData = value!;
    this.loadArchives();
  }
  get pageData(): SiaRouteData | undefined {
    return this._pageData;
  }

  closePanel() {
    this.visible = false;
  }
  async loadArchives() {
    this.archives = [];
    this.archivesDataSource = [];
    this.visible = false;
    this.path = null;
    this.selectedArchive = null;
    if (!this._pageData?.componentId) {
      return;
    }
    const archives = await this.siaFileSystemService.getArchives(
      this._pageData?.componentId,
    );
    if (!archives || archives.length == 0) {
      this.hasArchives.emit(false);
      this.manageAction();
      return;
    }
    this.archives = archives;
    this.manageAction();
    this.hasArchives.emit(true);
    this.processArchives();
  }
  async processArchives() {
    const promises = this.archives.map(async (archive) => {
      this.archivesDataSource.push({
        id: archive.id,
        description: archive.description,
      });
      let newPath = await Object.getPrototypeOf(execCode)
        .constructor(archive.scriptForPath)
        .bind(this)();
      archive.path = newPath;
    });

    await Promise.all(promises);
    let newPaths = JSON.stringify(this.archives.map((a) => a.path));
    if (this.#lastArchivesPaths == newPaths) {
      return;
    }
    this.#lastArchivesPaths = newPaths;
    this.archivesDataSource = jsonDeepClone(this.archivesDataSource);
    if (this.archives.length > 0) {
      this.selectedArchive = jsonDeepClone(this.archives[0]);
    }
    this.cdr.detectChanges();
  }
  private manageAction() {
    if (this.archives.length > 0) {
      this.siaBaseActionService.handleAction(
        this._pageData?.componentId ?? '',
        this._pageData?.guid ?? '',
        'file-system-panel',
        true,
      );
    } else {
      this.siaBaseActionService.handleAction(
        this._pageData?.componentId ?? '',
        this._pageData?.guid ?? '',
        'file-system-panel',
        false,
      );
    }
  }

  selectedArchiveChanged(event: string) {
    this.selectedArchive = this.archives.find((a) => a.id == event);
  }

  ngAfterViewInit() {
    this.archivesSelectHeight =
      this.selectBoxArchives?.element?.nativeElement?.offsetHeight;
  }

  ngOnDestroy() {
    this._subs.forEach((s) => s.unsubscribe());
    this._subs = [];
    this._pageChangedSub?.unsubscribe();
    super.ngOnDestroy();
  }
  ngOnInit() {
    super.ngOnInit();
    let s0 = this.siaActionsService
      .actionFired$()
      .subscribe((data: SiaActionFired) => {
        if (data.action?.componentId != this._pageData?.componentId) {
          return;
        }
        if (data.action?.id == 'file-system-panel') {
          this.visible = !this.visible;
        }
      });
    this._subs.push(s0);
    let s = this.siaParamsAndPatternsService.paramsHardUpdated$.subscribe(
      (guid: string) => {
        if (this.pageData && guid == this.pageData.guid) {
          this.processArchives();
        }
      },
    );
    this._subs.push(s);
    s = this.siaParamsAndPatternsService.departmentChanged$.subscribe(() => {
      this.processArchives();
    });
    this._subs.push(s);
    let s2 = this.siaRouterService.routeModeChanged$.subscribe((mode) => {
      if (
        mode.guid == this._pageData?.guid &&
        this._pageData?.mode != mode.mode
      ) {
        this._pageData.mode = mode.mode;
        this.processArchives();
      }
    });
    this._subs.push(s2);
  }
}

async function execCode() {}
